import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { MainNavigation } from '@studio/navigation';
import { NavLink } from 'react-router-dom';
import { selectUser, logout } from 'next/entities/user';
import { selectAccount } from 'next/entities/account';
import { selectAccounts } from 'next/entities/accounts';
import {
  BANNERS,
  JOURNEYS,
  LAUNCHPADS_V2,
  PINS,
  PINS_BETA,
  S5_GROUP_LIST,
  USER_LISTS_SEGMENTS,
  selectFeature,
} from 'next/entities/features';
import { selectSpoofing, start as startSpoofing } from 'next/entities/spoofing';
import { EXPERIMENTS } from 'constants/features';

const MainNavigationProvider = ({
  user,
  account,
  accountsCount,
  hasPins,
  hasExperiments,
  hasJourneys,
  hasBanners,
  hasLaunchpads,
  hasS5GroupList,
  isSpoofing,
  onSpoof,
  onLogout,
}) => {
  const navigationFeatureFlags = useMemo(
    () => ({
      hasPins,
      hasExperiments,
      hasJourneys,
      hasBanners,
      hasLaunchpads,
      hasS5GroupList,
      hasDevTools: user?.isAdmin,
      hasApiPlayground: user?.isAdmin,
    }),
    [
      hasPins,
      hasExperiments,
      hasJourneys,
      hasBanners,
      hasLaunchpads,
      hasS5GroupList,
      user?.isAdmin,
    ]
  );

  return (
    <MainNavigation
      user={user}
      account={account}
      accountsCount={accountsCount}
      onSpoof={onSpoof}
      onLogout={onLogout}
      isSpoofing={isSpoofing}
      NavLink={NavLink}
      featureFlags={navigationFeatureFlags}
      matchExactRoutes
    />
  );
};

MainNavigationProvider.propTypes = {
  user: PropTypes.shape({
    fullname: PropTypes.string,
    email: PropTypes.string,
    isAdmin: PropTypes.bool,
  }),
  account: PropTypes.shape({ name: PropTypes.string, id: PropTypes.string }),
  accountsCount: PropTypes.number,
  hasPins: PropTypes.bool,
  hasExperiments: PropTypes.bool,
  hasJourneys: PropTypes.bool,
  hasBanners: PropTypes.bool,
  hasLaunchpads: PropTypes.bool,
  hasS5GroupList: PropTypes.bool,
  isSpoofing: PropTypes.bool,
  onSpoof: PropTypes.func,
  onLogout: PropTypes.func,
};

const mapStateToProps = state => {
  const accounts = selectAccounts(state);
  const accountsCount = Object.keys(accounts ?? {}).length;

  return {
    user: selectUser(state),
    account: selectAccount(state),
    accountsCount,
    hasPins: selectFeature(state, PINS),
    hasPinsBeta: selectFeature(state, PINS_BETA),
    hasUserListsSegments: selectFeature(state, USER_LISTS_SEGMENTS),
    hasExperiments: selectFeature(state, EXPERIMENTS),
    hasJourneys: selectFeature(state, JOURNEYS),
    hasBanners: selectFeature(state, BANNERS),
    hasLaunchpads: selectFeature(state, LAUNCHPADS_V2),
    hasS5GroupList: selectFeature(state, S5_GROUP_LIST),
    isSpoofing: selectSpoofing(state),
  };
};

const mapDispatchToProps = {
  onSpoof: startSpoofing,
  onLogout: logout,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MainNavigationProvider);
